import { LIFE_COOLDOWN } from '../../Constants/Timers';
import { IGameState } from '../../State/Game/IGameState';
import {
    getCurrentLevelNumber,
    getLevelStarsTotal,
} from '../../State/Player/PlayerLevelHelper';
import { WORLDS } from '../../System/World';
import DateHelper from '../DateHelper';
import { AdDataSet } from './DataSet/Ad';
import { EntryPointDataSet } from './DataSet/EntryPoint';
import { EventDataSet } from './DataSet/Event';
import { MetaDataSet } from './DataSet/Meta';
import { MissionDataSet } from './DataSet/Mission';
import { TransactionDataSet } from './DataSet/Transaction';
import { AdDataHandler } from './Handler/Ad';
import { EntryPointDataHandler } from './Handler/EntryPoint';
import { EventDataHandler } from './Handler/Event';
import { MetaDataHandler } from './Handler/Meta';
import { MissionDataHandler } from './Handler/Mission';
import { TransactionDataHandler } from './Handler/Transaction';
import { IDataHandler } from './IDataHandler';
import { allTrackingEvents, TrackingEventName } from './TrackingEvent';

export enum TrackMethod {
    SET,
    ADD,
    SUBTRACT,
    MAX,
    MIN,
    STREAK,
    STREAK_END,
    ONCE,
}

export interface IDataSet {}

export let eventDataHandler: EventDataHandler<TrackingDataSet>;
export let metaDataHandler: MetaDataHandler;
export let missionDataHandler: MissionDataHandler;
export let transactionDataHandler: TransactionDataHandler;
export let dataHandlers: Array<IDataHandler<IDataSet>>;
export let entryPointDataHandler: EntryPointDataHandler;
export let adDataHandler: AdDataHandler;

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

type TrackingDataSet = Partial<
    Omit<EventDataSet<never>, 'eventParams'> &
    MetaDataSet &
    MissionDataSet &
    TransactionDataSet &
    AdDataSet &
    EntryPointDataSet
    >;

let trackingEventDataMap: {
    [key in allTrackingEvents]: IDataHandler<IDataSet>;
};

export let eventQueue: Array<EventDataSet<TrackingDataSet>> = [];

export function initializeTrackingDataHandlers() {
    metaDataHandler = new MetaDataHandler();
    missionDataHandler = new MissionDataHandler();
    transactionDataHandler = new TransactionDataHandler();
    eventDataHandler = new EventDataHandler<TrackingDataSet>();
    entryPointDataHandler = new EntryPointDataHandler();
    adDataHandler = new AdDataHandler();
    dataHandlers = [
        eventDataHandler,
        metaDataHandler,
        missionDataHandler,
        transactionDataHandler,
        entryPointDataHandler,
        adDataHandler,
    ];

    eventQueue = [];

    trackingEventDataMap = {
        missionStarted: missionDataHandler,
        missionCompleted: missionDataHandler,
        missionFailed: missionDataHandler,
        transaction: transactionDataHandler,
        resource: transactionDataHandler,
        newPlayer: entryPointDataHandler,
        gameStarted: entryPointDataHandler,
        adRequest: adDataHandler,
    };
}

export function getDataSetByKey(key: keyof TrackingDataSet): any {
    const handler = dataHandlers.find(dataHandler =>
        Object.prototype.hasOwnProperty.call(dataHandler.data, key)
    );

    if (handler !== undefined && handler.data) return handler.data;
}

export function track<T extends keyof TrackingDataSet>(
    key: T,
    value: TrackingDataSet[T],
    method: TrackMethod = TrackMethod.SET
) {
    try {
        const dataSet: any = getDataSetByKey(key);

        if (method === TrackMethod.SET || dataSet[key] === undefined) {
            dataSet[key] = value;
            return;
        }
        if (method === TrackMethod.ONCE) {
            if (!dataSet[key]) {
                dataSet[key] = value;
            }
        }

        if (typeof dataSet[key] === 'number' && typeof value === 'number') {
            switch (method) {
                case TrackMethod.ADD:
                    dataSet[key] += value;
                    break;
                case TrackMethod.SUBTRACT:
                    dataSet[key] -= value;
                    break;
                case TrackMethod.MAX:
                    dataSet[key] = Math.max(value, dataSet[key] as number);
                    break;
                case TrackMethod.MIN:
                    dataSet[key] = Math.min(value, dataSet[key] as number);
                    break;
                case TrackMethod.STREAK:
                    if (!streakValues[key]) streakValues[key] = 0;
                    streakValues[key] += value;
                    track(key, streakValues[key], TrackMethod.MAX);
                    break;
                case TrackMethod.STREAK_END:
                    streakValues[key] = 0;
            }
        }
    } catch (e) {
        console.info('Tracking exception: ', e);
    }
}

const streakValues: { [key in keyof TrackingDataSet]: number } = {};

export function sendEvent<T>(eventName: allTrackingEvents, state: IGameState) {
    try {
        syncGameStateWithTracking(state);
        eventQueue.push({
            ...eventDataHandler.getEventData(eventName),
            eventParams: {
                ...metaDataHandler.getEventData(eventName as TrackingEventName),
                ...trackingEventDataMap[eventName].getEventData(eventName),
            },
        });
    } catch (e) {
        console.info('Tracking exception: ', e);
    }
}

export function syncGameStateWithTracking(state: IGameState) {
    track('loadingTime', trackLoadingTime(), TrackMethod.ONCE);
    track('coins', state.playerState.coins);
    track('lives', state.playerState.lives);
    track('keysKeyHunt', state.playerState.keys);
    track('massRemoveBooster', state.playerState.inventory.boosterTNT);
    track('removeOneBooster', state.playerState.inventory.boosterBullsEye);
    track('changerBooster', state.playerState.inventory.boosterShuffle);
    track('jokerBooster', state.playerState.inventory.boosterJoker);
    track('stars', getLevelStarsTotal(state.playerState));
    track('realMoneyPurchasesMade', state.playerState.purchases);
    track('activeCardSet', state.playerState.backcard);
    track('activeMap', WORLDS[state.playerState.activeWorld - 1]);
    track('highestLevel', getCurrentLevelNumber(state));
    const timeDiff = DateHelper.getElapsedTime(state.playerState.lifeTimer);
    track('timeToNextLife', LIFE_COOLDOWN - timeDiff);
    track('musicIsOn', state.playerState.volumeMusic === 1);
    track('soundIsOn', state.playerState.volumeSFX === 1);
}

export function trackLoadingTime() {
    if (typeof window !== 'undefined') {
        return Date.now() - (window as any).JS_INIT_TIME;
    }
    return Date.now();
}
